import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Input, Switch } from '@/components/ui';
import { dateTime } from '@/lib/format';
import { addLeadFeed, getAudit, getLeadFeeds, setLeadFeedEnabled, type AuditEntry } from '@/data/client';
import { FormError } from '@/components/crm/Modal';

/* Which tritonIQ clients' leads flow into this workspace. For Triton that is
   Triton's own client record in tritonIQ. Nothing arrives until tritonIQ ships
   its side of the sync (docs/phase-1-plan.md, handoff list). */
export function LeadFeedSettings() {
  const qc = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ['leadFeeds'], queryFn: getLeadFeeds });
  const [clientId, setClientId] = useState('');
  const [label, setLabel] = useState('');
  const refresh = () => void qc.invalidateQueries({ queryKey: ['leadFeeds'] });
  const add = useMutation({ mutationFn: () => addLeadFeed(clientId.trim(), label.trim()), onSuccess: () => { setClientId(''); setLabel(''); refresh(); } });
  const toggle = useMutation({ mutationFn: (v: { clientId: string; enabled: boolean }) => setLeadFeedEnabled(v.clientId, v.enabled), onSuccess: refresh });

  return (
    <Card padding={false} className="crm-section crm-settings-card">
      <p className="crm-cell-muted" style={{ marginBottom: 14 }}>
        Leads from these tritonIQ clients arrive in the Leads inbox with their full source and campaign. Add Triton's own client
        (its Clerk organization id, <code>org_…</code>, from tritonIQ Admin). Leads will start flowing once tritonIQ's side of the sync is live.
      </p>
      {data.length > 0 && (
        <table className="dash-table" style={{ marginBottom: 16 }}>
          <thead><tr><th>Client</th><th>tritonIQ id</th><th className="crm-num">Leads</th><th>Active</th></tr></thead>
          <tbody>
            {data.map((f) => (
              <tr key={f.clientId}>
                <td>{f.label ?? '—'}</td>
                <td><code>{f.clientId}</code></td>
                <td className="crm-num">{f.leadCount}</td>
                <td><Switch checked={f.enabled} onChange={(e) => toggle.mutate({ clientId: f.clientId, enabled: e.target.checked })} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <form className="crm-invite" onSubmit={(e) => { e.preventDefault(); add.mutate(); }}>
        <Input label="Name" placeholder="Triton Commerce" value={label} onChange={(e) => setLabel(e.target.value)} />
        <Input label="tritonIQ client id" placeholder="org_…" value={clientId} onChange={(e) => setClientId(e.target.value)} />
        <Button type="submit" disabled={!clientId.trim() || add.isPending}>Connect</Button>
      </form>
      <FormError error={add.error ?? toggle.error} />
    </Card>
  );
}

const ACTION_LABEL: Record<string, string> = {
  'settings.update': 'Changed workspace settings', 'invite.created': 'Invited', 'invite.revoked': 'Revoked an invite',
  'member.role_change': 'Changed a role', 'member.removed': 'Removed a member', 'stage.created': 'Added a stage',
  'stage.renamed': 'Renamed a stage', 'stage.reordered': 'Reordered stages', 'stage.archived': 'Removed a stage',
  'lead_feed.added': 'Connected a leadIQ feed', 'import.companies': 'Imported companies', 'import.tritoniq_contacts': 'Imported contacts from tritonIQ', 'lead_feed.enabled': 'Resumed a leadIQ feed', 'lead_feed.disabled': 'Paused a leadIQ feed',
  'deal.attribution_override': 'Changed a won deal’s attribution', 'deal.reopened': 'Reopened a won deal',
};

function describe(e: AuditEntry): string {
  const d = e.detail ?? {};
  const bits = [d.email, d.role, d.from && d.to ? `${String(d.from)} → ${String(d.to)}` : null, d.name, d.label]
    .filter((x): x is string => typeof x === 'string' && !!x);
  return bits.join(' · ');
}

export function AuditLog() {
  const q = useInfiniteQuery({
    queryKey: ['audit'],
    queryFn: ({ pageParam }) => getAudit(pageParam),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (last) => (last.length === 100 ? last[last.length - 1].id : undefined),
  });
  const rows = q.data?.pages.flat() ?? [];
  return (
    <Card padding={false}>
      <table className="dash-table">
        <thead><tr><th>When</th><th>Who</th><th>What</th><th>Detail</th></tr></thead>
        <tbody>
          {rows.map((e) => (
            <tr key={e.id}>
              <td className="crm-cell-muted">{dateTime(e.at)}</td>
              <td>{e.user}</td>
              <td>{ACTION_LABEL[e.action] ?? e.action}</td>
              <td className="crm-cell-muted">{e.entity === 'deal' ? <Link to={`/deals/${e.entityId}`}>Open deal</Link> : describe(e)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {!rows.length && !q.isLoading && <p className="lead-none">Nothing recorded yet.</p>}
      {q.hasNextPage && <div className="crm-section"><Button variant="ghost" onClick={() => q.fetchNextPage()}>Load older</Button></div>}
    </Card>
  );
}
